/* Parsers for `validateSearch`, which every page loads with the route tree: zod there
   put 22 KB on every first load. An unusable value is dropped rather than thrown, so a
   hand-edited link still opens the page. */

/** A string, or undefined. The router parses `?token=123` as a number, so one is read back as
 * text. */
export function optionalString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

/** One of `options`, or undefined for anything else. */
/** A search box's text: trimmed, at most 120 characters, absent when empty. */
export function optionalSearch(value: unknown): string | undefined {
  return optionalString(value)?.trim().slice(0, 120) || undefined;
}

export function optionalOneOf<T extends string>(
  options: readonly T[],
  value: unknown,
): T | undefined {
  return options.find((option) => option === value);
}

/** An integer inside [min, max], or undefined. */
export function optionalInt(value: unknown, min: number, max: number): number | undefined {
  const number = typeof value === "string" ? Number(value) : value;
  if (typeof number !== "number" || !Number.isInteger(number)) return undefined;
  return number >= min && number <= max ? number : undefined;
}

/** True when the address carries the flag, otherwise undefined so the default stays out of it. */
export function optionalTrue(value: unknown): true | undefined {
  return value === true || value === "true" ? true : undefined;
}

/** Short text values for each of `keys`; empty or missing ones are left out. */
export function stringParams<K extends string>(
  keys: readonly K[],
  search: Record<string, unknown>,
): { [P in K]?: string } {
  const out: { [P in K]?: string } = {};
  for (const key of keys) out[key] = optionalString(search[key])?.trim().slice(0, 120) || undefined;
  return out;
}

/** The filters a page shows: the defaults, overridden by what the address carries. */
export function readFilters<T extends { [K in keyof T]: string }>(
  defaults: T,
  search: Partial<T>,
): T {
  const out = { ...defaults };
  for (const key of Object.keys(defaults) as Array<keyof T & string>) {
    const value = search[key];
    if (value !== undefined) out[key] = value as T[typeof key];
  }
  return out;
}

/** The address change for new filters: a value equal to its default leaves the address. */
export function filterPatch<T extends { [K in keyof T]: string }>(
  defaults: T,
  next: T,
): { [P in keyof T]?: string } {
  const out: { [P in keyof T]?: string } = {};
  for (const key of Object.keys(defaults) as Array<keyof T & string>) {
    out[key] = next[key] === defaults[key] ? undefined : next[key];
  }
  return out;
}

/** A yyyy-MM-dd day, or undefined. */
export function optionalDay(value: unknown): string | undefined {
  const text = optionalString(value);
  return text !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : undefined;
}

/** A local calendar day from yyyy-MM-dd. */
export function dayFromParam(day: string): Date {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1);
}

/** The values of a comma-separated address parameter. */
export function listParam(value: string | undefined): string[] {
  return value ? value.split(",") : [];
}

/** A yyyy-MM month, or undefined. */
export function optionalMonth(value: unknown): string | undefined {
  const text = optionalString(value);
  return text !== undefined && /^\d{4}-(0[1-9]|1[0-2])$/.test(text) ? text : undefined;
}

/** The value for the address: the "all" choice is the default and stays out of it. */
export const unlessAll = (value: string): string | undefined =>
  value === "all" ? undefined : value;

/** How a list writes its own address: the entry is replaced and the scroll kept, since a filter
 * typed or picked is not a new page. */
export const KEEP_PLACE = { replace: true, resetScroll: false } as const;

/** One writer for a list's address: `patch({ q: next })` merges into the current search with
 * KEEP_PLACE. Built from the route's own navigate, so each key keeps its type. */
export function searchPatch<S extends object>(
  navigate: (options: { search: (prev: S) => S } & typeof KEEP_PLACE) => Promise<void>,
): (next: Partial<S>) => void {
  return (next) => void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
}
